import React, { useState } from 'react';
import styled from 'styled-components';

const COLLAPSE_AT = 280;

const Summary = styled.div`
  font-size: 0.8125rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Toggle = styled.button`
  margin-top: 0.35rem;
  padding: 0;
  border: none;
  background: none;
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
`;

const Body = styled.pre`
  margin: 0.5rem 0 0;
  max-height: 16rem;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.75rem;
  line-height: 1.45;
`;

export interface ApiToolSummary {
  summary: string;
  body: string;
}

/** One line for a stored `API tool result` system message. */
export function summarizeApiToolResult(content: string): ApiToolSummary | null {
  const trimmed = (content || '').trim();
  if (!trimmed.startsWith('API tool result')) return null;
  const header = trimmed.match(
    /^API tool result for (.+?) \(([^)]+)\) operation=(\S+?):/,
  );
  const name = header?.[1]?.trim() || 'API';
  const operation = (header?.[3] || 'unknown').replace(/:$/, '');
  const newline = trimmed.indexOf('\n');
  const raw = newline >= 0 ? trimmed.slice(newline + 1).trim() : '';
  let okLabel = '';
  let statusLabel = '';
  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw) as { ok?: unknown; status?: unknown };
      if (typeof parsed.ok === 'boolean') okLabel = parsed.ok ? 'ok' : 'failed';
      if (parsed.status != null && parsed.status !== '') {
        statusLabel = String(parsed.status);
      }
    } catch {
      /* body stays hidden until expand */
    }
  }
  const summary = [name, operation, okLabel, statusLabel].filter(Boolean).join(' · ');
  return { summary, body: raw || trimmed };
}

export function prettyTrailBody(body: string): string {
  const trimmed = body.trim();
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return body;
    }
  }
  return body;
}

export const CollapsibleTrailText: React.FC<{ content: string }> = ({
  content,
}) => {
  const [open, setOpen] = useState(false);
  const tool = summarizeApiToolResult(content);
  if (tool) {
    return (
      <div>
        <Summary>{tool.summary}</Summary>
        <Toggle type="button" onClick={() => setOpen((value) => !value)}>
          {open ? 'Hide result' : 'Show result'}
        </Toggle>
        {open && <Body>{prettyTrailBody(tool.body)}</Body>}
      </div>
    );
  }

  const long = content.length > COLLAPSE_AT;
  const shown = !long || open ? content : `${content.slice(0, COLLAPSE_AT)}…`;
  return (
    <div>
      <Summary>{shown}</Summary>
      {long && (
        <Toggle type="button" onClick={() => setOpen((value) => !value)}>
          {open ? 'Show less' : 'Show more'}
        </Toggle>
      )}
    </div>
  );
};
